import { z } from "zod";
import { LabOrderSetRefSchema } from "./form-order-sets.js";
import { ManualOrderFormRefSchema } from "./manual-order-marker.js";
import { IdSchema, IsoDateTimeSchema, Sha256Schema } from "./primitives.js";
export const TerminologyAuthoritySchema = z.enum([
    "LOINC",
    "RadLex",
    "RxNorm",
    "NDC",
    "DailyMed",
    "local",
]);
export const TerminologyReleaseSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    version: z.number().int().positive(),
    authority: TerminologyAuthoritySchema,
    releaseName: z.string().min(1).max(160),
    releaseIdentifier: z.string().min(1).max(160),
    sourceUri: z.url().max(1_000),
    licenseIdentifier: z.string().min(1).max(160),
    status: z.enum(["staged", "active", "retired"]),
    importedAt: IsoDateTimeSchema,
    contentHash: Sha256Schema,
})
    .strict();
export const LabOrderInitiationModeSchema = z.enum([
    "manual",
    "profile",
    "ai_profile",
    "ai_custom",
]);
export const LabOrderProfileScopeSchema = z.enum(["personal", "clinic", "pathway"]);
export const LabOrderProfileStatusSchema = z.enum(["active", "inactive", "superseded"]);
export const LabOrderDeliveryMethodSchema = z.enum(["electronic", "print", "fax"]);
export const LabOrderTestCodeSystemSchema = z.enum(["LOINC", "local", "free_text"]);
export const LabOrderFastingSchema = z.enum(["required", "not_required", "unknown"]);
export const LabOrderItemSourceSchema = z
    .object({
    kind: z.enum(["manual", "profile", "ai"]),
    profileId: IdSchema.optional(),
    profileVersion: z.number().int().positive().optional(),
    evidenceSourceIds: z.array(IdSchema).max(50).default([]),
    explanation: z.string().min(1).max(1_000),
})
    .strict()
    .superRefine((source, context) => {
    const hasProfile = source.profileId !== undefined || source.profileVersion !== undefined;
    if (hasProfile && (source.profileId === undefined || source.profileVersion === undefined)) {
        context.addIssue({
            code: "custom",
            message: "Profile provenance requires both profileId and profileVersion",
        });
    }
    if (source.kind === "manual" && source.evidenceSourceIds.length > 0) {
        context.addIssue({
            code: "custom",
            path: ["evidenceSourceIds"],
            message: "Manual test selection cannot claim AI evidence provenance",
        });
    }
});
export const StructuredLabOrderTestSchema = z
    .object({
    code: z.string().min(1).max(80),
    display: z.string().min(1).max(240),
    codeSystem: LabOrderTestCodeSystemSchema,
    reason: z.string().min(1).max(1_000),
    specimen: z.string().min(1).max(160),
    fasting: LabOrderFastingSchema,
    source: LabOrderItemSourceSchema,
})
    .strict();
export const LabOrderQuestionAnswerSchema = z
    .object({
    questionId: z.string().regex(/^[a-z][a-z0-9_.-]{2,79}$/),
    prompt: z.string().min(1).max(500),
    answer: z.string().min(1).max(2_000).optional(),
    required: z.boolean(),
    source: z.enum(["clinician", "patient", "chart", "ai_inferred"]),
    evidenceSourceIds: z.array(IdSchema).max(50).default([]),
})
    .strict();
export const AppliedLabOrderProfileSchema = z
    .object({
    profileId: IdSchema,
    profileVersion: z.number().int().positive(),
    name: z.string().min(1).max(160),
    scope: LabOrderProfileScopeSchema,
    selectedBy: z.enum(["clinician", "ai"]),
    addedTestCodes: z.array(z.string().min(1).max(80)).max(100).default([]),
    removedTestCodes: z.array(z.string().min(1).max(80)).max(100).default([]),
})
    .strict();
const structuredLabOrderPayloadShape = {
    orderMode: LabOrderInitiationModeSchema,
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    terminologyReleaseId: IdSchema,
    terminologyReleaseVersion: z.number().int().positive(),
    appliedProfile: AppliedLabOrderProfileSchema.optional(),
    tests: z.array(StructuredLabOrderTestSchema).min(1).max(100),
    diagnosisCodes: z.array(z.string().min(1).max(80)).min(1).max(50),
    clinicalInformation: z.string().min(1).max(4_000),
    questions: z.array(LabOrderQuestionAnswerSchema).max(50).default([]),
    destinationId: IdSchema,
    requisitionTemplateId: IdSchema,
    requisitionTemplateVersion: z.number().int().positive(),
    deliveryMethod: LabOrderDeliveryMethodSchema,
    additionalInstructions: z.string().min(1).max(2_000).optional(),
};
function refineStructuredLabOrderPayload(payload, context) {
    const profileMode = payload.orderMode === "profile" || payload.orderMode === "ai_profile";
    if (profileMode !== (payload.appliedProfile !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["appliedProfile"],
            message: profileMode
                ? "Profile-based orders require an exact profile binding"
                : "Manual and custom AI orders cannot claim an applied profile",
        });
    }
    if (payload.orderMode === "profile" && payload.appliedProfile?.selectedBy !== "clinician") {
        context.addIssue({
            code: "custom",
            path: ["appliedProfile", "selectedBy"],
            message: "Clinician profile mode must be selected by a clinician",
        });
    }
    if (payload.orderMode === "ai_profile" && payload.appliedProfile?.selectedBy !== "ai") {
        context.addIssue({
            code: "custom",
            path: ["appliedProfile", "selectedBy"],
            message: "AI profile mode must preserve AI selection provenance",
        });
    }
    const testKeys = new Set();
    payload.tests.forEach((test, index) => {
        const key = `${test.codeSystem}\u0000${test.code}`;
        if (testKeys.has(key)) {
            context.addIssue({
                code: "custom",
                path: ["tests", index, "code"],
                message: "The same test cannot appear more than once",
            });
        }
        testKeys.add(key);
    });
    const questionIds = new Set();
    payload.questions.forEach((question, index) => {
        if (questionIds.has(question.questionId)) {
            context.addIssue({
                code: "custom",
                path: ["questions", index, "questionId"],
                message: "Order question identifiers must be unique",
            });
        }
        questionIds.add(question.questionId);
    });
}
/**
 * A lab order as a drafting model may propose it: exactly the earlier shape. The clinic order set
 * an order came from is recorded by the server only, so a model is never offered that field.
 */
export const ModelLabOrderPayloadSchema = z
    .object(structuredLabOrderPayloadShape)
    .strict()
    .superRefine(refineStructuredLabOrderPayload);
export const StructuredLabOrderPayloadSchema = z
    .object({
    ...structuredLabOrderPayloadShape,
    /** The clinic order set this order was drafted from; absent otherwise. */
    orderSet: LabOrderSetRefSchema.optional(),
    /**
     * The exact clinic form a physician picked for a "+" order, which always prints. Written by
     * the server like `orderSet`, so a drafting model is never offered it, and carried only by an
     * order the physician entered (`manual`). Absent on every other order, so its hash is
     * unchanged.
     */
    form: ManualOrderFormRefSchema.optional(),
})
    .strict()
    .superRefine(refineStructuredLabOrderPayload)
    .superRefine((payload, context) => {
    if (payload.form !== undefined && payload.orderMode !== "manual") {
        context.addIssue({
            code: "custom",
            path: ["form"],
            message: "Only an order the physician entered names the form it was picked on",
        });
    }
});
export const LabOrderProfileItemSchema = z
    .object({
    ordinal: z.number().int().positive(),
    code: z.string().min(1).max(80),
    display: z.string().min(1).max(240),
    codeSystem: LabOrderTestCodeSystemSchema,
    reason: z.string().min(1).max(1_000),
    specimen: z.string().min(1).max(160),
    fasting: LabOrderFastingSchema,
})
    .strict();
export const LabOrderProfileSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    version: z.number().int().positive(),
    scope: LabOrderProfileScopeSchema,
    ownerUserId: IdSchema.optional(),
    name: z.string().min(1).max(160),
    description: z.string().min(1).max(1_000),
    status: LabOrderProfileStatusSchema,
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    items: z.array(LabOrderProfileItemSchema).min(1).max(100),
    contentHash: Sha256Schema.optional(),
    createdAt: IsoDateTimeSchema,
})
    .strict()
    .superRefine((profile, context) => {
    if ((profile.scope === "personal") !== (profile.ownerUserId !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["ownerUserId"],
            message: "Only personal profiles require an ownerUserId",
        });
    }
});
export const LabCatalogItemSchema = z
    .object({
    code: z.string().min(1).max(80),
    display: z.string().min(1).max(240),
    codeSystem: LabOrderTestCodeSystemSchema,
    specimen: z.string().min(1).max(160),
    fasting: LabOrderFastingSchema,
    active: z.boolean(),
    orderQuestions: z
        .array(LabOrderQuestionAnswerSchema.omit({ answer: true, source: true }))
        .max(50),
})
    .strict();
export const LabCatalogSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    destinationId: IdSchema,
    version: z.number().int().positive(),
    terminologyRelease: TerminologyReleaseSchema,
    name: z.string().min(1).max(160),
    status: z.enum(["active", "inactive", "superseded"]),
    items: z.array(LabCatalogItemSchema).min(1),
    effectiveAt: IsoDateTimeSchema,
})
    .strict();
export const LabRequisitionTemplateSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    destinationId: IdSchema,
    version: z.number().int().positive(),
    name: z.string().min(1).max(160),
    sourceDocumentHash: Sha256Schema,
    fieldMap: z.record(z.string().min(1).max(120), z.string().min(1).max(240)),
    status: z.enum(["active", "inactive", "superseded"]),
    effectiveAt: IsoDateTimeSchema,
})
    .strict();
const RenderPartySchema = z
    .object({
    id: IdSchema,
    displayName: z.string().min(1).max(240),
    fields: z.record(z.string().min(1).max(120), z.string().max(500)),
})
    .strict();
export const LabRequisitionRenderContextSchema = z
    .object({
    patient: RenderPartySchema,
    clinic: RenderPartySchema,
    clinician: RenderPartySchema,
})
    .strict();
export const LabRequisitionPreviewSchema = z
    .object({
    templateId: IdSchema,
    templateVersion: z.number().int().positive(),
    destinationId: IdSchema,
    deliveryMethod: LabOrderDeliveryMethodSchema,
    patient: RenderPartySchema,
    clinic: RenderPartySchema,
    clinician: RenderPartySchema,
    tests: z.array(StructuredLabOrderTestSchema).min(1),
    diagnosisCodes: z.array(z.string().min(1).max(80)).min(1),
    clinicalInformation: z.string().min(1).max(4_000),
    answeredQuestions: z.array(LabOrderQuestionAnswerSchema),
    appliedProfile: AppliedLabOrderProfileSchema.optional(),
    additionalInstructions: z.string().min(1).max(2_000).optional(),
    contentHash: Sha256Schema,
})
    .strict();
export const SaveLabOrderProfileInputSchema = z
    .object({
    profileId: IdSchema.optional(),
    expectedVersion: z.number().int().min(0),
    scope: LabOrderProfileScopeSchema,
    name: z.string().min(1).max(160),
    description: z.string().min(1).max(1_000),
    status: z.enum(["active", "inactive"]),
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    items: z
        .array(LabOrderProfileItemSchema.omit({ ordinal: true }))
        .min(1)
        .max(100),
})
    .strict();
export const LabOrderConfigurationSchema = z
    .object({
    catalogs: z.array(LabCatalogSchema),
    profiles: z.array(LabOrderProfileSchema),
    templates: z.array(LabRequisitionTemplateSchema),
})
    .strict();
export const LabRequisitionManifestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    template: z
        .object({
        id: IdSchema,
        version: z.number().int().positive(),
        name: z.string().min(1).max(160),
        sourceDocumentHash: Sha256Schema,
        fieldMap: z.record(z.string(), z.string()),
    })
        .strict(),
    catalog: z
        .object({
        id: IdSchema,
        version: z.number().int().positive(),
        name: z.string().min(1).max(160),
        contentHash: Sha256Schema,
    })
        .strict(),
    destination: z
        .object({
        id: IdSchema,
        version: z.number().int().positive(),
        displayName: z.string().min(1).max(240),
        partner: z.string().min(1).max(80),
        externalIdentifier: z.string().min(1).max(240),
    })
        .strict(),
    patient: z
        .object({
        id: IdSchema,
        mrn: z.string().min(1).max(160),
        legalFirstName: z.string().min(1).max(160),
        legalLastName: z.string().min(1).max(160),
        preferredName: z.string().min(1).max(160).optional(),
        dateOfBirth: z.string().date(),
        administrativeSex: z.enum(["female", "male", "unknown", "other"]),
        phone: z.string().max(80).optional(),
        address: z.record(z.string(), z.unknown()),
    })
        .strict(),
    clinic: z
        .object({
        id: IdSchema,
        name: z.string().min(1).max(160),
        npi: z.string().max(80).optional(),
        phone: z.string().max(80).optional(),
        address: z.record(z.string(), z.unknown()),
        timezone: z.string().min(1).max(80),
    })
        .strict(),
    clinician: z
        .object({
        id: IdSchema,
        role: z.enum(["physician", "nurse_ma", "clinic_admin"]),
    })
        .strict(),
    order: z
        .object({
        actionId: IdSchema,
        actionVersion: z.number().int().positive(),
        mode: LabOrderInitiationModeSchema,
        appliedProfile: AppliedLabOrderProfileSchema.optional(),
        tests: z.array(StructuredLabOrderTestSchema).min(1).max(100),
        diagnosisCodes: z.array(z.string().min(1).max(80)).min(1).max(50),
        clinicalInformation: z.string().min(1).max(4_000),
        questions: z.array(LabOrderQuestionAnswerSchema).max(50),
        additionalInstructions: z.string().min(1).max(2_000).optional(),
        deliveryMethod: LabOrderDeliveryMethodSchema,
    })
        .strict(),
})
    .strict();
export const LabRequisitionManifestPreviewSchema = LabRequisitionManifestSchema.extend({
    contentHash: Sha256Schema,
}).strict();
export const PersistedLabOrderSchema = z
    .object({
    id: IdSchema,
    version: z.number().int().positive(),
    patientId: IdSchema,
    encounterId: IdSchema,
    bundleId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    acceptanceTransactionId: IdSchema,
    approvalReceiptId: IdSchema,
    fulfillmentId: IdSchema,
    resultExpectedEventId: IdSchema,
    state: z.enum([
        "locally_accepted",
        "queued",
        "submitted",
        "result_received",
        "closed",
        "cancelled",
    ]),
    payload: StructuredLabOrderPayloadSchema,
    payloadHash: Sha256Schema,
    createdAt: IsoDateTimeSchema,
    items: z.array(z
        .object({
        ordinal: z.number().int().positive(),
        codeSystem: LabOrderTestCodeSystemSchema,
        code: z.string().min(1).max(80),
        display: z.string().min(1).max(240),
        reason: z.string().min(1).max(1_000),
        specimen: z.string().min(1).max(160),
        fasting: LabOrderFastingSchema,
        source: LabOrderItemSourceSchema,
    })
        .strict()),
    requisition: z
        .object({
        id: IdSchema,
        version: z.number().int().positive(),
        outputKind: z.enum(["pdf", "structured_payload"]),
        state: z.enum(["prepared", "generated", "submitted", "failed", "cancelled"]),
        manifest: LabRequisitionManifestSchema,
        contentHash: Sha256Schema,
    })
        .strict(),
})
    .strict();
export const ImagingOrderInitiationModeSchema = z.enum(["manual", "ai_custom"]);
export const ImagingOrderDeliveryMethodSchema = z.enum(["electronic", "print", "fax"]);
export const ImagingModalitySchema = z.enum([
    "XR",
    "US",
    "CT",
    "MR",
    "MG",
    "NM",
    "FL",
    "DXA",
    "other",
]);
export const ImagingOrderCodeSystemSchema = z.enum(["RadLex", "LOINC", "local", "free_text"]);
export const ImagingLateralitySchema = z.enum([
    "left",
    "right",
    "bilateral",
    "none",
    "not_applicable",
    "unknown",
]);
export const ImagingContrastSchema = z.enum([
    "none",
    "with",
    "without",
    "with_and_without",
    "per_protocol",
    "unknown",
]);
export const PregnancyStatusSchema = z.enum([
    "not_applicable",
    "not_pregnant",
    "pregnant",
    "possible",
    "unknown",
]);
export const ImagingOrderItemSourceSchema = z
    .object({
    kind: z.enum(["manual", "ai"]),
    evidenceSourceIds: z.array(IdSchema).max(50).default([]),
    explanation: z.string().min(1).max(1_000),
})
    .strict()
    .superRefine((source, context) => {
    if (source.kind === "manual" && source.evidenceSourceIds.length > 0) {
        context.addIssue({
            code: "custom",
            path: ["evidenceSourceIds"],
            message: "Manual imaging selection cannot claim AI evidence provenance",
        });
    }
    if (source.kind === "ai" && source.evidenceSourceIds.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["evidenceSourceIds"],
            message: "AI-proposed imaging requires encounter evidence provenance",
        });
    }
});
export const StructuredImagingOrderStudySchema = z
    .object({
    code: z.string().min(1).max(80),
    display: z.string().min(1).max(240),
    codeSystem: ImagingOrderCodeSystemSchema,
    modality: ImagingModalitySchema,
    bodySite: z.string().min(1).max(160),
    laterality: ImagingLateralitySchema,
    contrast: ImagingContrastSchema,
    reason: z.string().min(1).max(1_000),
    source: ImagingOrderItemSourceSchema,
})
    .strict();
export const ImagingOrderPrerequisiteSchema = z
    .object({
    prerequisiteId: z.string().regex(/^[a-z][a-z0-9_.-]{2,79}$/),
    kind: z.enum([
        "renal_function",
        "pregnancy_screening",
        "metal_screening",
        "sedation_review",
        "prior_authorization",
        "policy",
        "other",
    ]),
    status: z.enum(["satisfied", "not_required", "unknown", "failed"]),
    evidenceSourceIds: z.array(IdSchema).max(50).default([]),
    detail: z.string().min(1).max(1_000),
})
    .strict();
export const ImagingDuplicateAssessmentSchema = z
    .object({
    status: z.enum(["clear", "possible_duplicate", "clinician_override"]),
    comparedOrderIds: z.array(IdSchema).max(50).default([]),
    rationale: z.string().min(1).max(1_000),
})
    .strict()
    .superRefine((assessment, context) => {
    if (assessment.status === "clinician_override" && assessment.comparedOrderIds.length === 0) {
        context.addIssue({
            code: "custom",
            path: ["comparedOrderIds"],
            message: "A duplicate override must identify the compared prior order",
        });
    }
});
export const StructuredImagingOrderPayloadSchema = z
    .object({
    orderMode: ImagingOrderInitiationModeSchema,
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    terminologyReleaseId: IdSchema,
    terminologyReleaseVersion: z.number().int().positive(),
    studies: z.array(StructuredImagingOrderStudySchema).min(1).max(20),
    diagnosisCodes: z.array(z.string().min(1).max(80)).min(1).max(50),
    clinicalQuestion: z.string().min(1).max(2_000),
    pregnancyStatus: PregnancyStatusSchema,
    prerequisites: z.array(ImagingOrderPrerequisiteSchema).max(50).default([]),
    duplicateAssessment: ImagingDuplicateAssessmentSchema,
    destinationId: IdSchema,
    requisitionTemplateId: IdSchema,
    requisitionTemplateVersion: z.number().int().positive(),
    deliveryMethod: ImagingOrderDeliveryMethodSchema,
    policyVersion: z.string().min(1).max(80),
    additionalInstructions: z.string().min(1).max(2_000).optional(),
})
    .strict()
    .superRefine((payload, context) => {
    const studyKeys = new Set();
    payload.studies.forEach((study, index) => {
        const key = `${study.codeSystem}\u0000${study.code}\u0000${study.laterality}`;
        if (studyKeys.has(key)) {
            context.addIssue({
                code: "custom",
                path: ["studies", index, "code"],
                message: "The same imaging study and laterality cannot appear more than once",
            });
        }
        studyKeys.add(key);
    });
    const prerequisiteIds = new Set();
    payload.prerequisites.forEach((prerequisite, index) => {
        if (prerequisiteIds.has(prerequisite.prerequisiteId)) {
            context.addIssue({
                code: "custom",
                path: ["prerequisites", index, "prerequisiteId"],
                message: "Imaging prerequisite identifiers must be unique",
            });
        }
        prerequisiteIds.add(prerequisite.prerequisiteId);
    });
});
export const ImagingCatalogItemSchema = z
    .object({
    code: z.string().min(1).max(80),
    display: z.string().min(1).max(240),
    codeSystem: ImagingOrderCodeSystemSchema.exclude(["free_text"]),
    modality: ImagingModalitySchema,
    bodySite: z.string().min(1).max(160),
    lateralityRequired: z.boolean(),
    allowedContrast: z.array(ImagingContrastSchema.exclude(["unknown"])).min(1),
    pregnancyCheckRequired: z.boolean(),
    requiredPrerequisites: z.array(ImagingOrderPrerequisiteSchema.shape.kind).max(20).default([]),
    active: z.boolean(),
})
    .strict();
/** One clinic-wide order concept learned from an administrator-reviewed form. */
export const ClinicImagingStudyConceptSchema = z
    .object({
    code: z.string().min(1).max(80),
    codeSystem: ImagingOrderCodeSystemSchema.exclude(["free_text"]),
    display: z.string().min(1).max(240),
    modality: ImagingModalitySchema,
    bodySite: z.string().min(1).max(160),
    lateralityRequired: z.boolean(),
    sourceDocumentId: IdSchema,
    sourceDocumentVersion: z.number().int().positive(),
    sourceDocumentVersionHash: Sha256Schema,
})
    .strict();
export const ClinicImagingStudyConceptListSchema = z
    .object({ studies: z.array(ClinicImagingStudyConceptSchema).max(5_000) })
    .strict();
export const ImagingFacilityFormReferenceSchema = z
    .object({
    documentId: IdSchema,
    documentVersion: z.number().int().positive(),
    documentVersionHash: Sha256Schema,
})
    .strict();
/** Clinic-admin approval of a destination's actual availability and safety policy. */
export const ClinicImagingStudyAssociationSchema = z
    .object({
    code: z.string().min(1).max(80),
    codeSystem: ImagingOrderCodeSystemSchema.exclude(["free_text"]),
    allowedContrast: z.array(ImagingContrastSchema.exclude(["unknown"])).min(1),
    pregnancyCheckRequired: z.boolean(),
    requiredPrerequisites: z.array(ImagingOrderPrerequisiteSchema.shape.kind).max(20),
})
    .strict();
export const ClinicImagingStudyAssociationReceiptSchema = z
    .object({
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    destinationId: IdSchema,
    addedCodes: z.array(z.string().min(1).max(80)),
})
    .strict();
export const AssociateClinicImagingStudiesCommandSchema = ImagingFacilityFormReferenceSchema.extend({
    siteId: IdSchema,
    catalogId: IdSchema,
    expectedCatalogVersion: z.number().int().positive(),
    studies: z.array(ClinicImagingStudyAssociationSchema).min(1).max(500),
    idempotencyKey: z.string().min(8).max(128),
}).strict();
export const ClinicImagingDestinationSchema = z
    .object({
    id: IdSchema,
    name: z.string().min(1).max(160),
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    form: ImagingFacilityFormReferenceSchema.optional(),
})
    .strict();
export const ClinicImagingDestinationListSchema = z
    .object({ destinations: z.array(ClinicImagingDestinationSchema).max(100) })
    .strict();
export const ImagingCatalogSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    destinationId: IdSchema,
    destinationName: z.string().trim().min(1).max(160).optional(),
    safetyPolicyVersion: z.string().trim().min(1).max(80).optional(),
    printableOnly: z.boolean().optional(),
    version: z.number().int().positive(),
    terminologyRelease: TerminologyReleaseSchema,
    name: z.string().min(1).max(160),
    status: z.enum(["active", "inactive", "superseded"]),
    items: z.array(ImagingCatalogItemSchema).min(1),
    effectiveAt: IsoDateTimeSchema,
})
    .strict();
/** Exact facility and published form selected by the clinician for this order. */
export const ImagingOrderDestinationSelectionSchema = z
    .object({
    destinationId: IdSchema,
    catalogId: IdSchema,
    catalogVersion: z.number().int().positive(),
    requisitionTemplateId: IdSchema,
    requisitionTemplateVersion: z.number().int().positive(),
    policyVersion: z.string().trim().min(1).max(80),
    form: ImagingFacilityFormReferenceSchema,
})
    .strict();
export const ImagingRequisitionTemplateSchema = z
    .object({
    id: IdSchema,
    tenantId: IdSchema,
    destinationId: IdSchema,
    form: ImagingFacilityFormReferenceSchema.optional(),
    version: z.number().int().positive(),
    name: z.string().min(1).max(160),
    sourceDocumentHash: Sha256Schema,
    fieldMap: z.record(z.string().min(1).max(120), z.string().min(1).max(240)),
    status: z.enum(["active", "inactive", "superseded"]),
    effectiveAt: IsoDateTimeSchema,
})
    .strict();
export const ImagingOrderConfigurationSchema = z
    .object({
    catalogs: z.array(ImagingCatalogSchema),
    templates: z.array(ImagingRequisitionTemplateSchema),
})
    .strict();
export const ImagingRequisitionManifestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    template: z
        .object({
        id: IdSchema,
        version: z.number().int().positive(),
        name: z.string().min(1).max(160),
        sourceDocumentHash: Sha256Schema,
    })
        .strict(),
    catalog: z
        .object({
        id: IdSchema,
        version: z.number().int().positive(),
        terminologyReleaseId: IdSchema,
        terminologyReleaseVersion: z.number().int().positive(),
    })
        .strict(),
    destination: z
        .object({
        id: IdSchema,
        version: z.number().int().positive(),
        displayName: z.string().min(1).max(240),
        partner: z.string().min(1).max(80),
        externalIdentifier: z.string().min(1).max(240),
    })
        .strict(),
    patient: z
        .object({
        id: IdSchema,
        mrn: z.string().min(1).max(160),
        legalFirstName: z.string().min(1).max(160),
        legalLastName: z.string().min(1).max(160),
        dateOfBirth: z.string().date(),
        administrativeSex: z.enum(["female", "male", "unknown", "other"]),
    })
        .strict(),
    clinic: z
        .object({
        id: IdSchema,
        name: z.string().min(1).max(160),
        timezone: z.string().min(1).max(80),
    })
        .strict(),
    clinician: z
        .object({ id: IdSchema, role: z.enum(["physician", "nurse_ma", "clinic_admin"]) })
        .strict(),
    order: StructuredImagingOrderPayloadSchema,
})
    .strict();
export const ImagingRequisitionManifestPreviewSchema = ImagingRequisitionManifestSchema.extend({
    contentHash: Sha256Schema,
}).strict();
export const PersistedImagingOrderSchema = z
    .object({
    id: IdSchema,
    version: z.number().int().positive(),
    patientId: IdSchema,
    encounterId: IdSchema,
    bundleId: IdSchema,
    actionId: IdSchema,
    actionVersion: z.number().int().positive(),
    acceptanceTransactionId: IdSchema,
    approvalReceiptId: IdSchema,
    fulfillmentId: IdSchema,
    reportExpectedEventId: IdSchema,
    state: z.enum([
        "locally_accepted",
        "queued",
        "submitted",
        "report_received",
        "closed",
        "cancelled",
    ]),
    payload: StructuredImagingOrderPayloadSchema,
    payloadHash: Sha256Schema,
    createdAt: IsoDateTimeSchema,
    requisition: z
        .object({
        id: IdSchema,
        version: z.number().int().positive(),
        outputKind: z.enum(["pdf", "structured_payload"]),
        state: z.enum(["prepared", "generated", "submitted", "failed", "cancelled"]),
        manifest: ImagingRequisitionManifestSchema,
        contentHash: Sha256Schema,
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=orders.js.map